// The question mark on every screen. It looks up the screen's help by route (each module's
// help.json, generated into help.generated.ts; the core's in help-core.json) and opens a sheet:
// what the screen is for and the steps. "Show me" scrolls to the control a step names and
// pulses it. A screen without help says so plainly instead of hiding the button.
import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { HELP, type HelpEntry } from '../help.generated';

export function helpFor(pathname: string): HelpEntry | null {
  const patterns = Object.keys(HELP).sort((a, b) => b.length - a.length);
  for (const p of patterns) { if (matchPath({ path: p, end: true }, pathname)) return HELP[p]; }
  return null;
}
/** Find the control a step means: a button or link whose words start with the target. */
export function showMe(target: string): boolean {
  const want = target.trim().toLowerCase();
  const els = Array.from(document.querySelectorAll<HTMLElement>('main button, main a, main [role="button"], main select, main input'));
  const hit = els.find(el => { const t = (el.innerText || el.getAttribute('aria-label') || el.getAttribute('placeholder') || '').trim().toLowerCase(); return t.startsWith(want) || t.includes(want); });
  if (!hit) return false;
  hit.scrollIntoView({ behavior: 'smooth', block: 'center' });
  hit.classList.add('tv-showme');
  setTimeout(() => hit.classList.remove('tv-showme'), 2600);
  return true;
}
export function HelpButton() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const entry = helpFor(pathname);
  useEffect(() => { setOpen(false); }, [pathname]);
  return (
    <>
      <button className="w-9 h-9 rounded-full bg-surface border border-line grid place-items-center shrink-0 font-disp font-bold text-[17px] text-muted" aria-label="How this screen works" onClick={() => setOpen(true)}>?</button>
      {open && <HelpSheet entry={entry} onClose={() => setOpen(false)} />}
    </>
  );
}
export function HelpSheet({ entry, onClose }: { entry: HelpEntry | null; onClose: () => void }) {
  const [missed, setMissed] = useState<number | null>(null);
  return (
    <div className="fixed inset-0 z-40 flex items-end md:items-center md:justify-center" style={{ background: 'var(--scrim, rgba(0,0,0,.5))' }} onClick={onClose}>
      <div className="bg-surface w-full md:max-w-[520px] rounded-t-[22px] md:rounded-[18px] px-4 pt-3 pb-7 flex flex-col gap-2.5 max-h-[85vh] overflow-y-auto" style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }} onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded bg-line mx-auto md:hidden" />
        {!entry ? (
          <>
            <h5 className="font-head font-bold text-[17px] m-0">No steps for this screen yet</h5>
            <p className="m-0 text-[13.5px] text-muted">The office screens get their cards in the next pass. The Guide under More has the written procedure.</p>
          </>
        ) : (
          <>
            <h5 className="font-head font-bold text-[17px] m-0">{entry.title}</h5>
            <p className="m-0 text-[13.5px] text-muted">{entry.purpose}</p>
            <ol className="m-0 pl-0 list-none flex flex-col gap-2">
              {entry.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-accent-soft text-accent font-disp font-bold text-[13px] grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="min-w-0 flex-1 text-[14px]">{s.text}{s.target && <> <button className="text-accent font-head font-semibold text-[13px] underline-offset-2 hover:underline" onClick={() => { const ok = showMe(s.target!); setMissed(ok ? null : i); if (ok) onClose(); }}>Show me</button>{missed === i && <small className="block text-muted text-[12px]">That control is not on the screen right now; it appears when the step applies.</small>}</>}</span>
                </li>
              ))}
            </ol>
          </>
        )}
        <div className="flex gap-2 mt-1"><button className="btn flex-1" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}
